import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  CheckCircle, XCircle, Clock, Eye, Package, Image as ImageIcon, Lightbulb,
  ArrowRight, User, Calendar, RefreshCw, AlertTriangle, Send,
  MessageSquare, Info, Edit2, RotateCcw
} from 'lucide-react';
import api from '../../utils/api';
import Modal from '../components/Modal';
import { useAuth } from '../../context/AuthContext';

const BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

const toSrc = (v) => {
  if (!v) return null;
  const s = String(v);
  if (s.startsWith('http')) return s;
  if (s.startsWith('/uploads/')) return `${BASE}${s}`;
  return null;
};

const isImageVal = (v) => {
  if (!v) return false;
  const s = String(v);
  return s.startsWith('/uploads/') || (s.startsWith('http') && /\.(jpg|jpeg|png|gif|webp)/i.test(s));
};

function ValDisplay({ value }) {
  const vals = Array.isArray(value) ? value : [value];
  const hasImages = vals.some(v => isImageVal(v));
  if (hasImages) {
    return (
      <div className="flex flex-wrap gap-2 mt-1">
        {vals.map((v, i) => {
          const src = toSrc(String(v));
          return src ? (
            <div key={i} className="relative group/img w-16 h-16 rounded-lg overflow-visible border border-gray-200 cursor-zoom-in shrink-0">
              <img src={src} alt="" className="w-16 h-16 object-cover rounded-lg transition-transform duration-300 group-hover/img:scale-110" />
              <div className="absolute left-full top-0 ml-2 z-[999] hidden group-hover/img:block pointer-events-none">
                <img src={src} alt="" className="w-52 h-52 object-cover rounded-xl shadow-2xl border-2 border-white" />
              </div>
            </div>
          ) : <span key={i} className="text-sm text-gray-700 break-all">{String(v)}</span>;
        })}
      </div>
    );
  }
  return <span className="text-sm text-gray-700 break-all">{vals.join(', ') || '—'}</span>;
}

const STATUS = {
  pending:  { color: 'text-amber-600 bg-amber-50 border-amber-200',      icon: Clock,        label: 'Pending' },
  approved: { color: 'text-emerald-600 bg-emerald-50 border-emerald-200', icon: CheckCircle,  label: 'Approved' },
  rejected: { color: 'text-rose-600 bg-rose-50 border-rose-200',          icon: XCircle,      label: 'Rejected' },
};

const TYPE_ICON = { product: Package, collection: ImageIcon, solution: Lightbulb };

/* ── Data diff component (shared) ── */
function DataDiff({ original, proposed, action }) {
  if (action === 'create') {
    return (
      <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
        <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-3">New Item to Create</p>
        <div className="space-y-2">
          {Object.entries(proposed || {}).filter(([k]) => !['__v','_id','createdBy','isActive'].includes(k)).map(([k, v]) => (
            <div key={k} className="flex gap-3 text-sm">
              <span className="text-gray-400 w-28 shrink-0 capitalize">{k.replace(/([A-Z])/g, ' $1')}</span>
              <ValDisplay value={v} />
            </div>
          ))}
        </div>
      </div>
    );
  }
  const keys = [...new Set([...Object.keys(original || {}), ...Object.keys(proposed || {})])]
    .filter(k => !['__v','_id','createdBy','updatedAt','createdAt'].includes(k));
  const changed = keys.filter(k => {
    const ov = Array.isArray(original?.[k]) ? original[k].join(',') : String(original?.[k] ?? '');
    const nv = Array.isArray(proposed?.[k]) ? proposed[k].join(',') : String(proposed?.[k] ?? '');
    return ov !== nv;
  });
  return (
    <div className="space-y-3">
      {changed.length === 0 && <p className="text-xs text-gray-400 italic">No field changes detected</p>}
      {changed.map(k => (
        <div key={k} className="rounded-xl overflow-visible border border-gray-100">
          <div className="bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider capitalize">
            {k.replace(/([A-Z])/g, ' $1')}
          </div>
          <div className="grid grid-cols-2 divide-x divide-gray-100">
            <div className="p-3 bg-red-50">
              <p className="text-xs text-red-400 mb-1 font-medium">Current (Live)</p>
              <ValDisplay value={original?.[k]} />
            </div>
            <div className="p-3 bg-emerald-50">
              <p className="text-xs text-emerald-500 mb-1 font-medium">Proposed Change</p>
              <ValDisplay value={proposed?.[k]} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   MASTER ADMIN VIEW — full review with approve/reject
   ══════════════════════════════════════════════════════════════════════════ */
function MasterAdminSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [filter, setFilter]   = useState('pending');
  const [selected, setSelected] = useState(null);
  const [note, setNote]       = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const load = () => {
    setFetching(true);
    api.get(`/submissions?status=${filter}`)
      .then(r => setSubmissions(r.data.submissions || []))
      .catch(() => {})
      .finally(() => setFetching(false));
  };
  useEffect(() => { load(); }, [filter]);

  const review = async (status) => {
    setLoading(true);
    try {
      await api.put(`/submissions/${selected._id}/review`, { status, adminNote: note });
      toast.success(status === 'approved' ? '✅ Approved & published to frontend' : '❌ Submission rejected');
      setSelected(null); setNote(''); load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error reviewing submission');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 pb-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Submissions</h1>
          <p className="text-sm text-gray-400 mt-0.5">Review, approve or reject manager submissions</p>
        </div>
        <button onClick={load} className="text-gray-400 hover:text-gray-600 transition-colors">
          <RefreshCw size={16} className={fetching ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="flex gap-2">
        {['pending', 'approved', 'rejected'].map(s => {
          const { color, icon: Icon, label } = STATUS[s];
          return (
            <button key={s} onClick={() => setFilter(s)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold border transition-all
                ${filter === s ? color + ' border' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}>
              <Icon size={12} /> {label}
            </button>
          );
        })}
      </div>

      {submissions.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col items-center justify-center py-16 gap-3 text-gray-300">
          <Clock size={32} />
          <p className="text-sm">No {filter} submissions</p>
        </div>
      ) : (
        <div className="space-y-3">
          {submissions.map(sub => {
            const TypeIcon = TYPE_ICON[sub.type] || Package;
            const st = STATUS[sub.status];
            const isEdit = sub.action === 'edit';
            return (
              <div key={sub._id}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center shrink-0">
                  <TypeIcon size={18} className="text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm font-semibold text-gray-800 capitalize">{sub.type}</span>
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${st.color}`}>
                      <st.icon size={10} /> {st.label}
                    </span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${isEdit ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-purple-50 text-purple-600 border-purple-200'}`}>
                      {isEdit ? 'Edit Request' : 'New Item'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    <span className="flex items-center gap-1"><User size={10} /> {sub.user?.name || 'Unknown'}</span>
                    <span className="flex items-center gap-1"><Calendar size={10} /> {new Date(sub.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                  </div>
                  {sub.data?.title && (
                    <p className="text-xs text-gray-500 mt-1 truncate flex items-center gap-1">
                      {isEdit ? (
                        <>
                          <span className="line-through text-red-400">{sub.originalData?.title}</span>
                          <ArrowRight size={10} className="text-gray-300" />
                          <span className="text-emerald-600 font-medium">{sub.data.title}</span>
                        </>
                      ) : sub.data.title}
                    </p>
                  )}
                  {sub.adminNote && (
                    <p className="text-xs text-gray-400 mt-1 italic flex items-center gap-1">
                      <MessageSquare size={10} /> {sub.adminNote}
                    </p>
                  )}
                </div>
                <button onClick={() => { setSelected(sub); setNote(''); }}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl transition-colors shrink-0">
                  <Eye size={12} /> Review
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Review Modal */}
      {selected && (
        <Modal title={`Review ${selected.type} ${selected.action === 'edit' ? 'Edit' : 'Creation'}`} onClose={() => setSelected(null)} wide>
          <div className="space-y-5">
            <div className="flex items-center gap-4 bg-gray-50 rounded-xl p-4">
              <div className="w-10 h-10 rounded-xl bg-white border border-gray-100 flex items-center justify-center">
                {React.createElement(TYPE_ICON[selected.type] || Package, { size: 18, className: 'text-gray-400' })}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800 capitalize">{selected.type} — {selected.action === 'edit' ? 'Edit Request' : 'New Item'}</p>
                <p className="text-xs text-gray-400">
                  Submitted by <strong>{selected.user?.name}</strong> ({selected.user?.email}) on {new Date(selected.createdAt).toLocaleString()}
                </p>
              </div>
              <span className={`ml-auto inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border ${STATUS[selected.status].color}`}>
                {React.createElement(STATUS[selected.status].icon, { size: 11 })} {STATUS[selected.status].label}
              </span>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                {selected.action === 'edit' ? 'Changes Requested' : 'Data to Create'}
              </p>
              <DataDiff original={selected.originalData} proposed={selected.data} action={selected.action} />
            </div>
            {selected.status === 'pending' && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    Admin Note <span className="font-normal text-gray-400">(optional — shown to manager)</span>
                  </label>
                  <textarea rows={2} value={note} onChange={e => setNote(e.target.value)}
                    placeholder="Add a note for the manager…"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-yellow-400 resize-none" />
                </div>
                <div className="flex gap-3 pt-2 border-t border-gray-50">
                  <button onClick={() => review('approved')} disabled={loading}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50">
                    {loading ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                    Approve & Publish
                  </button>
                  <button onClick={() => review('rejected')} disabled={loading}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50">
                    {loading ? <RefreshCw size={14} className="animate-spin" /> : <XCircle size={14} />}
                    Reject
                  </button>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 flex items-start gap-2">
                  <AlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    <strong>Approve</strong> will immediately publish this {selected.action === 'edit' ? 'edit' : 'new item'} to the frontend.
                    <strong> Reject</strong> will keep the current live data unchanged and notify the manager.
                  </p>
                </div>
              </>
            )}
            {selected.status !== 'pending' && selected.adminNote && (
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-400 mb-1">Admin Note</p>
                <p className="text-sm text-gray-700">{selected.adminNote}</p>
                {selected.reviewedBy && (
                  <p className="text-xs text-gray-400 mt-2">Reviewed by {selected.reviewedBy?.name} on {new Date(selected.reviewedAt).toLocaleString()}</p>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   MANAGER VIEW — read-only tracker of their own submissions
   ══════════════════════════════════════════════════════════════════════════ */
function ManagerSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [filter, setFilter]     = useState('');
  const [selected, setSelected] = useState(null);
  const [resubmitSub, setResubmitSub] = useState(null);   // submission being edited for resubmit
  const [editData, setEditData] = useState({});            // editable fields
  const [resubmitting, setResubmitting] = useState(false);
  const [fetching, setFetching] = useState(false);

  const load = () => {
    setFetching(true);
    const url = filter ? `/submissions?status=${filter}` : '/submissions';
    api.get(url)
      .then(r => setSubmissions(r.data.submissions || []))
      .catch(() => {})
      .finally(() => setFetching(false));
  };
  useEffect(() => { load(); }, [filter]);

  const openResubmit = (sub) => {
    setResubmitSub(sub);
    // Pre-fill editable fields from the rejected submission's data
    setEditData({ ...sub.data });
  };

  const handleResubmit = async () => {
    setResubmitting(true);
    try {
      await api.post(`/submissions/${resubmitSub._id}/resubmit`, { data: editData });
      toast.success('Resubmitted for Master Admin approval');
      setResubmitSub(null);
      setEditData({});
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error resubmitting');
    } finally { setResubmitting(false); }
  };

  const pending  = submissions.filter(s => s.status === 'pending').length;
  const approved = submissions.filter(s => s.status === 'approved').length;
  const rejected = submissions.filter(s => s.status === 'rejected').length;

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">My Submissions</h1>
          <p className="text-sm text-gray-400 mt-0.5">Track the status of your submitted changes</p>
        </div>
        <button onClick={load} className="text-gray-400 hover:text-gray-600 transition-colors">
          <RefreshCw size={16} className={fetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Pending Review', value: pending,  color: '#F59E0B', bg: '#FFFBEB', icon: Clock },
          { label: 'Approved',       value: approved, color: '#10B981', bg: '#F0FDF4', icon: CheckCircle },
          { label: 'Rejected',       value: rejected, color: '#F43F5E', bg: '#FFF1F2', icon: XCircle },
        ].map(({ label, value, color, bg, icon: Icon }) => (
          <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: bg }}>
              <Icon size={20} style={{ color }} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{value}</p>
              <p className="text-xs text-gray-400 uppercase tracking-wider">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2">
        {[
          { val: '',         label: 'All',      icon: Send },
          { val: 'pending',  label: 'Pending',  icon: Clock },
          { val: 'approved', label: 'Approved', icon: CheckCircle },
          { val: 'rejected', label: 'Rejected', icon: XCircle },
        ].map(({ val, label, icon: Icon }) => (
          <button key={val} onClick={() => setFilter(val)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold border transition-all
              ${filter === val
                ? val === '' ? 'bg-gray-800 text-white border-gray-800'
                  : STATUS[val]?.color + ' border'
                : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}>
            <Icon size={12} /> {label}
          </button>
        ))}
      </div>

      {/* Submissions list */}
      {submissions.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col items-center justify-center py-16 gap-3 text-gray-300">
          <Send size={32} />
          <p className="text-sm">No submissions yet</p>
          <p className="text-xs text-gray-300">When you add or edit products/collections, they'll appear here for approval</p>
        </div>
      ) : (
        <div className="space-y-3">
          {submissions.map(sub => {
            const TypeIcon = TYPE_ICON[sub.type] || Package;
            const st = STATUS[sub.status];
            const isEdit = sub.action === 'edit';
            return (
              <div key={sub._id}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center shrink-0">
                  <TypeIcon size={18} className="text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm font-semibold text-gray-800 capitalize">{sub.type}</span>
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${st.color}`}>
                      <st.icon size={10} /> {st.label}
                    </span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${isEdit ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-purple-50 text-purple-600 border-purple-200'}`}>
                      {isEdit ? 'Edit Request' : 'New Item'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    <span className="flex items-center gap-1"><Calendar size={10} /> {new Date(sub.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                  </div>
                  {sub.data?.title && (
                    <p className="text-xs text-gray-600 mt-1 font-medium truncate">{sub.data.title}</p>
                  )}
                  {/* Admin note — shown when reviewed */}
                  {sub.adminNote && (
                    <div className={`mt-2 flex items-start gap-1.5 text-xs rounded-lg px-3 py-2 ${sub.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                      <MessageSquare size={11} className="shrink-0 mt-0.5" />
                      <span><strong>Admin note:</strong> {sub.adminNote}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {/* Edit & Resubmit — only for rejected */}
                  {sub.status === 'rejected' && (
                    <button onClick={() => openResubmit(sub)}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-yellow-600 hover:bg-yellow-700 rounded-xl transition-colors">
                      <Edit2 size={12} /> Edit & Resubmit
                    </button>
                  )}
                  <button onClick={() => setSelected(sub)}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl transition-colors">
                    <Eye size={12} /> View
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Modal — read-only for manager */}
      {selected && (
        <Modal title={`${selected.type} ${selected.action === 'edit' ? 'Edit Request' : 'New Item'} — Details`} onClose={() => setSelected(null)} wide>
          <div className="space-y-5">
            {/* Status banner */}
            <div className={`flex items-center gap-3 rounded-xl px-4 py-3 border ${STATUS[selected.status].color}`}>
              {React.createElement(STATUS[selected.status].icon, { size: 16 })}
              <div>
                <p className="text-sm font-semibold">{STATUS[selected.status].label}</p>
                <p className="text-xs opacity-75">
                  {selected.status === 'pending' && 'Awaiting Master Admin review'}
                  {selected.status === 'approved' && 'Your change has been published to the frontend'}
                  {selected.status === 'rejected' && 'Your change was not approved'}
                </p>
              </div>
              <span className="ml-auto text-xs opacity-60">{new Date(selected.createdAt).toLocaleString()}</span>
            </div>

            {/* Admin note */}
            {selected.adminNote && (
              <div className={`flex items-start gap-3 rounded-xl px-4 py-3 border ${selected.status === 'approved' ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                <MessageSquare size={16} className={selected.status === 'approved' ? 'text-emerald-600 shrink-0' : 'text-rose-600 shrink-0'} />
                <div>
                  <p className="text-xs font-semibold mb-0.5">Message from Master Admin</p>
                  <p className="text-sm">{selected.adminNote}</p>
                  {selected.reviewedAt && (
                    <p className="text-xs opacity-60 mt-1">Reviewed on {new Date(selected.reviewedAt).toLocaleString()}</p>
                  )}
                </div>
              </div>
            )}

            {/* What you submitted */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                {selected.action === 'edit' ? 'Your Requested Changes' : 'Data You Submitted'}
              </p>
              <DataDiff original={selected.originalData} proposed={selected.data} action={selected.action} />
            </div>

            {/* Info note */}
            <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 flex items-start gap-2">
              <Info size={14} className="text-blue-400 shrink-0 mt-0.5" />
              <p className="text-xs text-blue-600">
                {selected.status === 'pending' && 'Your submission is in the queue. Master Admin will review it shortly.'}
                {selected.status === 'approved' && 'This change is now live on the website.'}
                {selected.status === 'rejected' && 'This change was not approved. You can make a new submission with corrections.'}
              </p>
            </div>
          </div>
        </Modal>
      )}
      {/* Edit & Resubmit Modal */}
      {resubmitSub && (
        <Modal title={`Edit & Resubmit — ${resubmitSub.type}`} onClose={() => setResubmitSub(null)} wide>
          <div className="space-y-5">
            {/* Rejection context */}
            <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 flex items-start gap-2">
              <XCircle size={14} className="text-rose-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-rose-700 mb-0.5">Rejected by Master Admin</p>
                {resubmitSub.adminNote && (
                  <p className="text-xs text-rose-600"><strong>Reason:</strong> {resubmitSub.adminNote}</p>
                )}
              </div>
            </div>

            {/* Editable fields */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Edit Your Submission</p>
              <div className="space-y-3">
                {Object.entries(editData)
                  .filter(([k]) => !['__v','_id','createdBy','isActive','images','adminNote','status','reviewedBy','reviewedAt'].includes(k))
                  .map(([key, val]) => (
                    <div key={key}>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 capitalize">
                        {key.replace(/([A-Z])/g, ' $1')}
                      </label>
                      {typeof val === 'string' && val.length > 60 ? (
                        <textarea
                          value={editData[key] || ''}
                          onChange={e => setEditData(d => ({ ...d, [key]: e.target.value }))}
                          rows={3}
                          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-yellow-400 resize-none"
                        />
                      ) : (
                        <input
                          value={editData[key] || ''}
                          onChange={e => setEditData(d => ({ ...d, [key]: e.target.value }))}
                          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-yellow-400"
                        />
                      )}
                    </div>
                  ))}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 flex items-start gap-2">
              <Info size={14} className="text-blue-400 shrink-0 mt-0.5" />
              <p className="text-xs text-blue-600">
                Your edited submission will be sent back to Master Admin for review. The original rejected submission will remain in your history.
              </p>
            </div>

            <div className="flex gap-3 pt-2 border-t border-gray-50">
              <button onClick={() => setResubmitSub(null)}
                className="flex-1 py-3 border border-gray-200 text-gray-600 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleResubmit} disabled={resubmitting}
                className="flex-1 flex items-center justify-center gap-2 py-3 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #B8A830, #d4af37)' }}>
                {resubmitting ? <RefreshCw size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                {resubmitting ? 'Resubmitting…' : 'Resubmit for Approval'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
export default function AdminSubmissions() {
  const { isMasterAdmin } = useAuth();
  return isMasterAdmin ? <MasterAdminSubmissions /> : <ManagerSubmissions />;
}
